import React, { useState, useCallback, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import styled from 'styled-components';

import Modal from '../../../components/modal/Modal';
import ConfirmModal from '../../../components/modal/ConfirmModal';
import Button from '../../../components/button/Button';
import Input from '../../../components/form-item/Input';
import FileUploader from '../../../components/file-uploader/FileUploader';
import UploadProgressBar from '../../../components/upload-progress/UploadProgressBar';
import MediaItemTable, { MediaTableRow } from '../../../components/media-item-table/MediaItemTable';
import MediaTranscriptionModal from '../../../components/media-preview/components/MediaTranscriptionModal';
import BulkTranscriptionModal from '../../../components/transcription/BulkTranscriptionModal';
import { isTranscribableFileType } from '../../../components/transcription/transcriptionTargets';
import useTranscriptionStatuses from '../../../hooks/useTranscriptionStatuses';

import TagSelector from '../../../components/tag-selector/TagSelector';
import useS3Upload, { UploadingFile, UseS3UploadReturn, ValidationError } from '../../../hooks/useS3Upload';
import {
  MediaItem,
  PRODUCER_TAG_LABELS,
  PRODUCER_TAG_TONES,
  CLIENT_TAG_LABELS,
  useCreateMediaItemMutation,
  useUpdateMediaItemMutation,
  useDeleteMediaItemMutation,
} from '../../../app/services/mediaItems';
import {
  useUpdateShotMutation,
} from '../../../app/services/shots';
import { useGetProjectQuery } from '../../../app/services/projects';
import { useGetProjectGitHubRepositoriesQuery } from '../../../app/services/github';
import RepoFetchModal from './RepoFetchModal';
import { variationNameForUpload } from '../../../utils/maSequenceFile';
import { downloadFile } from '../../../utils/downloadFile';
import { normalizeNfc } from '../../../utils/text';
import { getMediaUrl } from '../previewMediaUtils';
import { MaWarning } from '../ma/maTypes';

// ===== Types =====

interface VariationGroup {
  /** updated_at 降順（先頭 = 最新テイク = バリエーション行として表示） */
  items: MediaItem[];
}

/** MA シーケンス（CSV / RPP）関連の表示情報（Preview の useMaSequence から渡される） */
export interface ShotEditMaInfo {
  /** シーケンスにリンクされたメディアアイテム ID（「MA」ラベル表示対象） */
  linkedItemIds: Set<string>;
  /** アクティブな MA シーケンスファイルのアイテム ID */
  activeMaFileId: string | null;
  /** シーケンスのパース / リンク警告 */
  warnings: MaWarning[];
  /** シーケンスファイルの取得エラー */
  fetchError: string | null;
}

/** 確認・エラー文言に出すメディアアイテム名（バリエーション名、未設定はファイル名） */
const mediaItemLabel = (item: MediaItem): string => item.variation_name || item.file_name;

// ===== Styled Components =====

const Section = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

/** アップロードを受け付けるファイル種別（FileUploader / メディア表 D&D 共通） */
const MEDIA_ACCEPT = {
  'video/*': [],
  'audio/*': [],
  'image/*': [],
  'application/pdf': [],
  'text/csv': ['.csv'],
  'application/octet-stream': ['.rpp'],
};

const MEDIA_TABLE_HELP = `ファイルは表へのドラッグ＆ドロップ、または末尾の「+」行から追加できます。
同じ名前で追加したメディアはテイク（履歴）として下にぶら下がります。
「名前_a」「名前_b」のように末尾にアルファベットを付けると、オプション（候補）のグループとしてまとめて比較表示されます。
名前・ファイル名はクリックで編集できます。履歴を別の名前にリネームすると、独立したバリエーション（既存名なら別グループの履歴）になります。
並び替えは行頭のハンドル、またはヘッダー（名前・ファイル名・更新日時）のクリックで行えます。
ダウンロード・文字起こし・削除は行末の3点リーダーから行えます。音声・動画をまとめて文字起こしする場合は表の上の「一括文字起こし」を使います。
CSV（media, start, end, offset, track, gain 列）や Reaper プロジェクト（RPP）をアップロードすると MA シーケンスとして扱われ、紐づいたメディア（「MA」ラベル）は動画単位のミックス再生で確認できます。
MA シーケンスファイル名に「_v1.1.0」のようなバージョンが含まれる場合、バージョンより前が同じファイルは同一テイク履歴として積まれます。`;

/** MA 警告リストの表示ボックス */
const MaWarningBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 12px;
  border: 1px solid var(--soremo-border);
  border-left: 3px solid #e6a23c;
  border-radius: 4px;
  background: var(--soremo-background, #f5f6f7);
  max-height: 120px;
  overflow-y: auto;
`;

// ===== Main Modal =====

export interface ShotEditModalProps {
  projectId: string;
  shotId: string;
  shotName: string;
  mediaItems: MediaItem[];
  initialTagIds?: string[];
  /** MA シーケンス（CSV）関連の表示情報 */
  maInfo?: ShotEditMaInfo | null;
  onClose: () => void;
  onSaved: () => void;
}

const ShotEditModal: React.FC<ShotEditModalProps> = ({
  projectId,
  shotId,
  shotName: initialShotName,
  mediaItems: initialMediaItems,
  initialTagIds = [],
  maInfo = null,
  onClose,
  onSaved,
}) => {
  // ===== State =====
  const [shotName, setShotName] = useState(initialShotName);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(initialTagIds);
  const [localItems, setLocalItems] = useState<MediaItem[]>(
    () => [...initialMediaItems].sort((a, b) => a.variation_order - b.variation_order),
  );
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  // 削除確認モーダルの対象（トークルームのファイル一覧と同じく実行前に確認する）
  const [deleteTarget, setDeleteTarget] = useState<MediaItem | null>(null);
  // 文字起こし（単体 = 行メニュー / 一括 = ショット内の音声・動画すべて）
  const [transcribeTarget, setTranscribeTarget] = useState<MediaItem | null>(null);
  const [showBulkTranscription, setShowBulkTranscription] = useState(false);
  // リポジトリから最新を取得（プロジェクトに GitHub リポジトリがリンクされているとき）
  const [showRepoFetch, setShowRepoFetch] = useState(false);
  // このモーダルセッション中に新規アップロードしたメディアの ID 一覧。
  // アップロードは silent（通知メール対象外）で作成し、「更新して通知」の
  // 保存時に silent を解除して通知を解放する（「通知せず更新」では解除しない）。
  const newUploadIdsRef = useRef<string[]>([]);

  // ===== Project tags =====
  const { data: projectData } = useGetProjectQuery(
    { project_id: projectId },
    { skip: !projectId }
  );
  const shotTags = projectData?.data?.project?.shot_tags || [];

  // ===== GitHub repositories（「リポジトリから最新を取得」ボタンの表示判定） =====
  const { data: repoData } = useGetProjectGitHubRepositoriesQuery(projectId, {
    skip: !projectId,
  });
  const githubRepositories = useMemo(
    () => repoData?.data?.repositories ?? [],
    [repoData],
  );

  // ===== API Mutations =====
  const [updateShot] = useUpdateShotMutation();
  const [createMediaItem] = useCreateMediaItemMutation();
  const [updateMediaItem] = useUpdateMediaItemMutation();
  const [deleteMediaItem] = useDeleteMediaItemMutation();

  // ===== S3 Upload =====
  // onFileComplete は S3 転送完了後（= isUploading が落ちたあと）に走りうるため、
  // メディアアイテム作成のあいだも「処理中」として扱えるよう withPostProcess を
  // ref 経由で参照する（フック自身の戻り値をオプション内で使うため）
  const withPostProcessRef = useRef<
    UseS3UploadReturn['withPostProcess'] | null
  >(null);

  const {
    uploadFiles,
    uploadingFiles,
    isUploading,
    abortFile,
    retryFile,
    postProcessLabel,
    withPostProcess,
  } = useS3Upload({
    purpose: 'media_item',
    projectId,
    shotId,
    skipRegister: true,
    onValidationError: (errors: ValidationError[]) => {
      errors.forEach(e => console.warn(`バリデーションエラー: ${e.file.name} - ${e.message}`));
    },
    onFileComplete: async (file: UploadingFile) => {
      if (!file.fileKey) return;
      const register = async () => {
        try {
          const result = await createMediaItem({
            shot_id: shotId,
            file_key: file.fileKey!,
            variation_name: variationNameForUpload(file.file.name),
            // 通知メールバッチは即時配信ユーザーに毎分走るため、アップロード
            // 時点では silent で作成し、通知するかは保存ボタンで決める
            silent: true,
          }).unwrap();
          setLocalItems(prev => [...prev, result.data.media_item]);
          newUploadIdsRef.current.push(result.data.media_item.id);
        } catch (err) {
          // 登録エラー（例: 他ショット参照のバリエーションへのテイク追加は不可）を通知
          console.error('メディアアイテム作成エラー:', err);
          const detail = (err as { data?: { message?: string } })?.data?.message;
          toast.error(
            `「${file.file.name}」を登録できませんでした${detail ? `（${detail}）` : ''}`,
          );
        }
      };
      await (withPostProcessRef.current
        ? withPostProcessRef.current(register, 'メディアを登録中')
        : register());
    },
  });
  withPostProcessRef.current = withPostProcess;

  // ===== Variation grouping =====
  // 表示順は localItems の配列順を信頼する（D&D で並び替え可能）。
  // variation_order は「サーバー上の現在値」を保持し、handleSave での diff
  // 検出（新しい順序 vs サーバー値）に使うので、ここでは並び替えに使わない。
  const variationGroups = useMemo<VariationGroup[]>(() => {
    const groupMap = new Map<string, MediaItem[]>();
    // NFD / NFC の表記ゆれは同じバリエーション（Preview のスライドと同じキー）
    for (const item of localItems) {
      const key = normalizeNfc(item.variation_name);
      const existing = groupMap.get(key);
      if (existing) {
        existing.push(item);
      } else {
        groupMap.set(key, [item]);
      }
    }

    const groups: VariationGroup[] = [];
    for (const items of groupMap.values()) {
      const sorted = [...items].sort(
        (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
      );
      groups.push({ items: sorted });
    }

    return groups;
  }, [localItems]);

  // ===== テーブル行（バリエーション = 最新テイク / 履歴 = 過去テイク） =====
  const tableRows = useMemo<MediaTableRow[]>(() => {
    const rows: MediaTableRow[] = [];
    for (const group of variationGroups) {
      const total = group.items.length;
      group.items.forEach((item, idx) => {
        // MA ステータス: シーケンスファイル本体 / シーケンスにリンクされたメディア
        const isActiveMaFile = maInfo?.activeMaFileId === item.id;
        const isMaLinked = !isActiveMaFile && !!maInfo?.linkedItemIds.has(item.id);
        // 他ショット参照（グローバルアセット）は参照元ショット名を表示
        const refSource = item.reference_source;
        const referenceLabel = refSource
          ? `${isMaLinked ? 'MA・' : ''}参照: ${refSource.shot_name ?? '他ショット'}`
          : null;
        rows.push({
          id: item.id,
          kind: idx === 0 ? 'variation' : 'history',
          name: item.variation_name,
          fileName: item.file_name,
          fileType: item.file_type,
          thumbnail: item.thumbnail,
          mediaSrc: getMediaUrl(item),
          updatedAt: item.updated_at,
          // テイク番号は古い順に 1, 2, ...（最新 = total）
          takeNumber: total - idx,
          producerTag: item.producer_tag ? PRODUCER_TAG_LABELS[item.producer_tag] : null,
          producerTagTone: item.producer_tag ? PRODUCER_TAG_TONES[item.producer_tag] : undefined,
          clientTag: item.client_tag ? CLIENT_TAG_LABELS[item.client_tag] : null,
          // 「MA」= MA シーケンス経由で表示・再生 / 「参照」= 他ショット参照
          statusLabel: isActiveMaFile
            ? 'MAシーケンス'
            : referenceLabel ?? (isMaLinked ? 'MA' : null),
          statusLabelTone: isActiveMaFile || referenceLabel ? 'outline' : 'blue',
        });
      });
    }
    return rows;
  }, [variationGroups, maInfo]);

  // ===== 文字起こし =====
  // ショット編集の「フォルダ単位」= このショットのメディア全件。テイク（履歴）も
  // それぞれ独立したファイルなので対象に含める。
  const transcriptionTargets = useMemo(
    () =>
      localItems
        .filter(item => isTranscribableFileType(item.file_type))
        .map(item => ({ fileId: item.id, durationSeconds: item.duration ?? null })),
    [localItems],
  );
  const transcribableFileIds = useMemo(
    () => transcriptionTargets.map(t => t.fileId),
    [transcriptionTargets],
  );
  const transcriptionStatuses = useTranscriptionStatuses(transcribableFileIds);

  const handleTranscribe = useCallback(
    (mediaItemId: string) => {
      const target = localItems.find(item => item.id === mediaItemId);
      if (target) setTranscribeTarget(target);
    },
    [localItems],
  );

  // ===== Handlers =====

  // FileUploader の onUpload に渡すコールバック。
  // ファイル選択直後に 1 ファイルずつ即時アップロードする。
  // 終了後に selectedFiles から取り除き、FileUploader 内のリストもクリーンに保つ。
  const handleUpload = useCallback(
    async (file: File) => {
      try {
        await uploadFiles([file]);
      } finally {
        setSelectedFiles(prev => prev.filter(f => f !== file));
      }
    },
    [uploadFiles],
  );

  // メディア表への直接ドロップ / 末尾「+」行のファイル選択で追加されたファイルを
  // まとめて即時アップロードする（アップロード完了ごとに onFileComplete で行が増える）
  const handleFilesAdded = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      void uploadFiles(files);
    },
    [uploadFiles],
  );

  /**
   * 指定アイテム群の variation_name を一括変更する共通処理。
   * ローカル state を楽観的に更新し、各アイテムを PATCH する。
   */
  const renameItems = useCallback(
    async (itemIds: string[], newName: string) => {
      const idSet = new Set(itemIds);
      let snapshot: MediaItem[] = [];
      setLocalItems(prev => {
        snapshot = prev;
        return prev.map(item =>
          idSet.has(item.id) ? { ...item, variation_name: newName } : item,
        );
      });
      try {
        await Promise.all(
          itemIds.map(id =>
            updateMediaItem({
              shot_id: shotId,
              media_item_id: id,
              variation_name: newName,
            }).unwrap(),
          ),
        );
      } catch (err) {
        console.error('バリエーション名更新エラー:', err);
        setLocalItems(snapshot);
      }
    },
    [shotId, updateMediaItem],
  );

  /**
   * リネーム。
   * - バリエーション行（グループの最新テイク）: 履歴を含むグループ全体を
   *   一括リネームし、「最新だけ改名されて履歴と分裂する」ことを防ぐ
   * - 履歴行: そのアイテムだけリネームする。ユニーク名なら独立した
   *   バリエーションに、既存名なら別グループの履歴に移動できる
   */
  const handleRename = useCallback(
    (mediaItemId: string, newName: string) => {
      const target = localItems.find(item => item.id === mediaItemId);
      if (!target) return;
      const group = variationGroups.find(g =>
        g.items.some(item => item.id === mediaItemId),
      );
      const isLatest = group && group.items[0].id === mediaItemId;
      const ids = isLatest ? group.items.map(item => item.id) : [target.id];
      renameItems(ids, newName);
    },
    [localItems, variationGroups, renameItems],
  );

  /**
   * ファイル名（表示名）のインライン編集。
   * ローカル state を楽観的に更新し、PATCH（BE では display_file_name に保存）する。
   */
  const handleRenameFile = useCallback(
    async (mediaItemId: string, newFileName: string) => {
      let snapshot: MediaItem[] = [];
      setLocalItems(prev => {
        snapshot = prev;
        return prev.map(item =>
          item.id === mediaItemId ? { ...item, file_name: newFileName } : item,
        );
      });
      try {
        await updateMediaItem({
          shot_id: shotId,
          media_item_id: mediaItemId,
          file_name: newFileName,
        }).unwrap();
      } catch (err) {
        console.error('ファイル名更新エラー:', err);
        setLocalItems(snapshot);
      }
    },
    [shotId, updateMediaItem],
  );

  /** 3点リーダー「ダウンロード」。元ファイル（original_url）を保存する */
  const handleDownload = useCallback(
    (mediaItemId: string) => {
      const target = localItems.find(item => item.id === mediaItemId);
      if (!target) return;
      void downloadFile(target.original_url, target.file_name);
    },
    [localItems],
  );

  /** 並び替え（D&D / ヘッダーソート）で確定したバリエーション id の並び順を localItems に反映 */
  const handleReorder = useCallback(
    (orderedVariationIds: string[]) => {
      // variation_order は更新しない（サーバー値を保持）。
      // localItems の並び順を変えることで variationGroups の出現順が変わり
      // UI の並び替えが反映される。handleSave で「サーバー値 vs 新しい順序」
      // の diff を計算して、変わった item だけ PATCH する。
      const groupByLatestId = new Map(
        variationGroups.map(group => [group.items[0].id, group]),
      );
      const newItems: MediaItem[] = [];
      orderedVariationIds.forEach(id => {
        const group = groupByLatestId.get(id);
        if (group) newItems.push(...group.items);
      });
      if (newItems.length !== localItems.length) return;
      setLocalItems(newItems);
    },
    [variationGroups, localItems.length],
  );

  // ===== リポジトリから最新を取得 =====

  /** アクティブな MA シーケンスファイル（RPP の更新判定の基準） */
  const activeMaFile = useMemo(
    () =>
      maInfo?.activeMaFileId
        ? localItems.find(item => item.id === maInfo.activeMaFileId) ?? null
        : null,
    [maInfo, localItems],
  );

  /**
   * ウィザードで作成されたメディアアイテム（silent 登録済み）を一覧に反映する。
   * アップロードと同じく newUploadIdsRef に積み、「更新して通知」で silent を解除する
   */
  const handleRepoImported = useCallback((items: MediaItem[]) => {
    if (items.length === 0) return;
    setLocalItems(prev => [...prev, ...items]);
    newUploadIdsRef.current.push(...items.map(item => item.id));
  }, []);

  /** 3点リーダー「削除」。まず確認モーダルを開く */
  const handleDeleteRequest = useCallback(
    (mediaItemId: string) => {
      const target = localItems.find(item => item.id === mediaItemId);
      if (target) setDeleteTarget(target);
    },
    [localItems],
  );

  /** 削除確認後の実行。楽観更新し、API 失敗時は rollback する */
  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    const mediaItemId = deleteTarget.id;
    setDeleteTarget(null);
    // optimistic 更新の前にスナップショットを取り、失敗時に rollback する
    let snapshot: MediaItem[] = [];
    setLocalItems(prev => {
      snapshot = prev;
      return prev.filter(item => item.id !== mediaItemId);
    });
    try {
      await deleteMediaItem({
        shot_id: shotId,
        media_item_id: mediaItemId,
      }).unwrap();
    } catch (err) {
      console.error('メディアアイテム削除エラー:', err);
      // API 失敗時は UI を元に戻し、失敗したことを利用者に伝える
      setLocalItems(snapshot);
      const detail = (err as { data?: { message?: string } })?.data?.message;
      toast.error(
        `「${mediaItemLabel(deleteTarget)}」を削除できませんでした${detail ? `（${detail}）` : ''}`,
      );
    }
  }, [deleteTarget, shotId, deleteMediaItem]);

  /**
   * 保存処理。
   * @param notify true = 「更新して通知」: 新規アップロード分の silent を解除し、
   *   通知メール対象として解放する。false = 「通知せず更新」: silent のまま保存し、
   *   メール通知を送らない。
   */
  const handleSave = useCallback(async (notify: boolean) => {
    setIsSaving(true);
    try {
      const hasNewUploads = newUploadIdsRef.current.length > 0;
      await updateShot({
        project_id: projectId,
        shot_id: shotId,
        shot_name: shotName,
        tag_ids: selectedTagIds,
        ...(hasNewUploads && { status: 2 }),
      }).unwrap();

      // variation_order は variation group 単位の順序。各 group 内の全 take に
      // 同じ newOrder を割り当てる（Preview の handleSidebarDragEnd と統一）。
      const orderUpdates: { item: MediaItem; newOrder: number }[] = [];
      variationGroups.forEach((group, idx) => {
        const newOrder = idx + 1;
        group.items.forEach(item => {
          if (item.variation_order !== newOrder) {
            orderUpdates.push({ item, newOrder });
          }
        });
      });

      // 通知する場合のみ、セッション中の新規アップロード分の silent を解除する
      // （削除済みアイテムへの PATCH を避けるため localItems に残っている ID に限定）
      const releaseIds = new Set(
        notify
          ? newUploadIdsRef.current.filter(id => localItems.some(item => item.id === id))
          : [],
      );

      // 同一アイテムへの並び順更新と silent 解除は 1 リクエストにまとめる
      // （並列 PATCH の後勝ち保存でフィールドが巻き戻るのを防ぐ）
      const patchBodies = new Map<string, { variation_order?: number; silent?: boolean }>();
      orderUpdates.forEach(({ item, newOrder }) => {
        patchBodies.set(item.id, { variation_order: newOrder });
      });
      releaseIds.forEach(id => {
        patchBodies.set(id, { ...patchBodies.get(id), silent: false });
      });

      await Promise.all(
        Array.from(patchBodies.entries()).map(([id, body]) =>
          updateMediaItem({
            shot_id: shotId,
            media_item_id: id,
            ...body,
          }).unwrap(),
        ),
      );

      onSaved();
      onClose();
    } catch (err) {
      console.error('保存エラー:', err);
    } finally {
      setIsSaving(false);
    }
  }, [projectId, shotId, shotName, selectedTagIds, variationGroups, localItems, updateShot, updateMediaItem, onSaved, onClose]);

  const saveDisabled = isSaving || isUploading || !shotName.trim();

  return (
    <Modal
      onClose={onClose}
      title="ショット編集"
      size="wide"
      footer={
        <>
          <Button size="small" theme="tertiary" variant="text" onClick={onClose}>
            キャンセル
          </Button>
          {/* 右端: 通知せず更新（メール通知なし）と 更新して通知（従来の更新） */}
          <div className="u-row u-gap8 u-items-center">
            <Button
              size="small"
              theme="secondary"
              variant="text"
              rightIcon="chevron_right"
              onClick={() => handleSave(false)}
              disabled={saveDisabled}
            >
              通知せず更新
            </Button>
            <Button
              size="small"
              theme="primary"
              variant="filled"
              rightIcon="chevron_right"
              onClick={() => handleSave(true)}
              disabled={saveDisabled}
            >
              {isSaving ? "送信中..." : "更新して通知"}
            </Button>
          </div>
        </>
      }
    >
      {/* ショット名 */}
      <Section>
        <Input
          label="ショット名"
          option="required"
          value={shotName}
          onChange={e => setShotName(e.target.value)}
          placeholder="ショット名を入力"
        />
      </Section>

      {/* タグ */}
      {shotTags.length > 0 && (
        <TagSelector
          tags={shotTags}
          selectedIds={selectedTagIds}
          onChange={setSelectedTagIds}
        />
      )}

      {/* アップロード（メディアが無いときのみ全面表示。
          以降の追加は表への直接 D&D と末尾の「+」行から行う） */}
      {localItems.length === 0 && (
        <Section>
          <FileUploader
            files={selectedFiles}
            onChange={setSelectedFiles}
            multiple
            accept={MEDIA_ACCEPT}
            height={120}
            placeholder="メディアをアップロード（MAシーケンス CSV / RPP も可）"
            onUpload={handleUpload}
            disabled={isUploading}
          />
        </Section>
      )}

      {/* MA シーケンスの警告（リンク失敗・パースエラー等） */}
      {maInfo && (maInfo.fetchError || maInfo.warnings.length > 0) && (
        <Section>
          <MaWarningBox>
            <span className="label8 u-text-gray">MAシーケンスの警告</span>
            {maInfo.fetchError && (
              <span className="bodytext-11 u-text-gray">{maInfo.fetchError}</span>
            )}
            {maInfo.warnings.map((w, i) => (
              <span key={i} className="bodytext-11 u-text-gray">
                {w.line != null ? `${w.line}行目: ` : ''}
                {w.message}
              </span>
            ))}
          </MaWarningBox>
        </Section>
      )}

      {/* メディアアイテム一覧（バリエーション / 履歴の表形式）。
          表全体がドロップゾーンで、末尾の「+」行からファイル選択もできる */}
      <Section>
        {(githubRepositories.length > 0 || transcriptionTargets.length > 0) && (
          <div className="u-row u-w100 u-justify-end u-gap8">
            {githubRepositories.length > 0 && (
              <Button
                size="small"
                theme="secondary"
                variant="text"
                width="fit"
                leftIcon="cloud_download"
                onClick={() => setShowRepoFetch(true)}
                disabled={isUploading}
              >
                リポジトリから最新を取得
              </Button>
            )}
            {transcriptionTargets.length > 0 && (
              <Button
                size="small"
                theme="secondary"
                variant="text"
                width="fit"
                leftIcon="speech_to_text"
                onClick={() => setShowBulkTranscription(true)}
              >
                {`一括文字起こし（${transcriptionTargets.length}件）`}
              </Button>
            )}
          </div>
        )}
        <MediaItemTable
          rows={tableRows}
          onRename={handleRename}
          onRenameFile={handleRenameFile}
          onReorder={handleReorder}
          onDownload={handleDownload}
          onDelete={handleDeleteRequest}
          onTranscribe={handleTranscribe}
          transcriptionStatuses={transcriptionStatuses}
          emptyMessage="メディアアイテムがありません"
          helpText={MEDIA_TABLE_HELP}
          onFilesSelected={handleFilesAdded}
          accept={MEDIA_ACCEPT}
          addFilesDisabled={isUploading}
          uploadingRow={
            uploadingFiles.length > 0 || postProcessLabel ? (
              <UploadProgressBar
                files={uploadingFiles}
                onCancel={abortFile}
                onRetry={(file) => void retryFile(file.id)}
                compact
                postProcessLabel={postProcessLabel}
              />
            ) : null
          }
        />
      </Section>

      {/* リポジトリから最新を取得ウィザード */}
      {showRepoFetch && (
        <RepoFetchModal
          projectId={projectId}
          shotId={shotId}
          shotName={shotName}
          mediaItems={localItems}
          activeMaFile={activeMaFile}
          repositories={githubRepositories}
          onClose={() => setShowRepoFetch(false)}
          onImported={handleRepoImported}
        />
      )}

      {/* 文字起こし（単体） */}
      {transcribeTarget && (
        <MediaTranscriptionModal
          fileId={transcribeTarget.id}
          fileName={transcribeTarget.file_name}
          onClose={() => setTranscribeTarget(null)}
        />
      )}

      {/* 文字起こし（ショット内のメディア全件） */}
      {showBulkTranscription && (
        <BulkTranscriptionModal
          targets={transcriptionTargets}
          scopeLabel={`ショット「${shotName}」のメディア`}
          onClose={() => setShowBulkTranscription(false)}
        />
      )}

      {/* 削除確認モーダル */}
      {deleteTarget && (
        <ConfirmModal
          message={`「${mediaItemLabel(deleteTarget)}」を削除しますか？`}
          confirmLabel="削除"
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </Modal>
  );
};

export default ShotEditModal;
